import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useOrg } from '../app/OrgProvider';
import { displayNameOf, useAuth } from '../auth/AuthProvider';
import { Wordmark } from '../components/Clapper';
import { AppLink, useGo } from '../components/Nav';
import {
  CameraIcon,
  MicIcon,
  MonitorIcon,
  PauseIcon,
  PlayIcon,
  RestartIcon,
  StopIcon,
  UploadIcon,
  WaveformIcon,
  XIcon,
} from '../components/icons';
import { Banner, Button, Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { paywallPath } from '../lib/plans';
import { clock, defaultRecordingTitle } from '../lib/format';
import { useHotkeys } from '../lib/useHotkeys';
import { fetchMe } from '../queries/account';
import { takeViewPreference } from '../lib/storage';
import { orgAcceptsRecordings } from '../queries/orgs';
import { createUploadTransport } from '../queries/videos';
import { FileUpload } from './FileUpload';
import { RecorderPreview } from './RecorderPreview';
import { Countdown } from './RecorderStage';
import { cornerFor } from './composer';
import { useRecorderSession, type RecordSurface, type RecorderApi } from './useRecorder';

/**
 * The recorder, over whatever page you are on.
 *
 * There is no recording page. Record dims the app and puts the same panel
 * the Mac app has in the top right: a source, a camera, a microphone, and
 * one red button. Start picks the screen through the browser's own dialog,
 * which is the one thing a browser will not let us do ahead of time, then
 * counts down. While a take runs the tab is covered: what is being captured
 * plays in the middle with the controls under it. Minimize folds that to a
 * strip with the clock and the three controls, lifts the cover so the page
 * underneath can be used and shown, and leaves the camera in a bubble in
 * the corner; the choice is remembered for the next take. Stop lands on the
 * share page, the way the Mac app opens it, since the link is what a
 * recording is for.
 *
 * The overlay lives in the app shell, so a take survives moving between
 * pages; it is the route that changes, not the recorder.
 */

/** Which door was opened: a take recorded here, or a file brought in. */
type OverlayMode = 'record' | 'upload';

/** How the recorder was reached, in the shared vocabulary's words. */
export type RecorderEntry = 'button' | 'hotkey' | 'deep_link';

interface OverlayContextValue {
  isOpen: boolean;
  open: (entry?: RecorderEntry) => void;
  /** The upload door: any video in, a Slate recording out. A paid seat feature. */
  openUpload: () => void;
  close: () => void;
}

const OverlayContext = createContext<(OverlayContextValue & { mode: OverlayMode | null }) | null>(null);

export function RecorderOverlayProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<OverlayMode | null>(null);
  const value = useMemo(
    () => ({
      mode,
      isOpen: mode !== null,
      open: (entry: RecorderEntry = 'button') => {
        track('Recorder Opened', { entry });
        setMode('record');
      },
      openUpload: () => setMode('upload'),
      close: () => setMode(null),
    }),
    [mode],
  );
  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>;
}

export function useRecorderOverlay(): OverlayContextValue {
  const value = useContext(OverlayContext);
  if (!value) throw new Error('useRecorderOverlay must be used inside <RecorderOverlayProvider>.');
  return value;
}

export function RecorderOverlay() {
  const value = useContext(OverlayContext);
  // Whether the address on the account has been confirmed, and whether the
  // week of grace has run out. Asked here, above both doors, because both
  // lead to the same API call and the same refusal.
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000, enabled: Boolean(value?.mode) });
  if (!value?.mode) return null;
  const overdue =
    me.data?.emailVerified === false && me.data.verifyBy !== null && new Date(me.data.verifyBy).getTime() <= Date.now();
  if (overdue) return <VerifyPanel email={me.data?.email ?? ''} onClose={value.close} />;
  if (value.mode === 'upload') return <UploadPanel onClose={value.close} />;
  return <RecorderPanel onClose={value.close} />;
}

/**
 * The wall an unconfirmed address meets after its week: the same shape as
 * the paid-feature pitch, worded for this. The API refuses the recording
 * either way; this saves somebody a take that would not upload.
 */
function VerifyPanel({ email, onClose }: { email: string; onClose: () => void }) {
  const { sendVerification } = useAuth();
  const send = useMutation({ mutationFn: sendVerification });
  useHotkeys({ escape: onClose });
  return (
    <Backdrop onDismiss={onClose}>
      <Card className="w-full max-w-md">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-body font-medium text-chalk">Confirm your email address</p>
          <CloseButton onClick={onClose} />
        </div>
        <div className="rounded-md border border-hairline bg-slate-900 px-5 py-8 text-center">
          <p className="text-body text-chalk">Recording is paused until {email} is confirmed</p>
          <p className="mx-auto mt-1 max-w-sm text-caption text-chalk-dim">
            Your account has run for a week on an address nobody has confirmed. Open the link in the confirmation
            email and everything carries on where it was, recordings included.
          </p>
          <Button
            variant="primary"
            className="mt-4"
            busy={send.isPending}
            disabled={send.isSuccess}
            onClick={() => {
              track('Paywall Opened', { feature: 'verify' });
              send.mutate();
            }}
          >
            {send.isSuccess ? 'Sent. Check your inbox.' : 'Send the confirmation email'}
          </Button>
          {send.isError ? (
            <p className="mt-3 text-caption text-danger">{errorMessage(send.error)}</p>
          ) : null}
        </div>
      </Card>
    </Backdrop>
  );
}

// ---------------------------------------------------------------------------

/**
 * A video that already exists, in; a Slate recording, with its transcript,
 * title and summary, out. That is the paid seat's door, so without one the
 * panel is the pitch rather than the drop zone. The API refuses the upload
 * either way; this just keeps someone from choosing a file first.
 */
function UploadPanel({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const go = useGo();
  const transport = useMemo(() => createUploadTransport(), []);
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000 });
  const fallbackTitle = useCallback(() => defaultRecordingTitle(displayNameOf(user)), [user]);

  useHotkeys({ escape: onClose });

  return (
    <Backdrop onDismiss={onClose}>
      <Card className="w-full max-w-md">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-body font-medium text-chalk">Upload a video</p>
          <CloseButton onClick={onClose} />
        </div>
        {me.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner size={18} className="text-chalk-faint" />
          </div>
        ) : me.data?.paidSeat ? (
          <>
            <FileUpload
              transport={transport}
              source="upload"
              makeTitle={(file) => file.name.replace(/\.[^.]+$/, '') || fallbackTitle()}
              onUploaded={(result) => {
                onClose();
                go('/v/$shareId', { params: { shareId: result.shareId } });
              }}
            />
            <p className="mt-3 text-caption text-chalk-faint">
              It becomes a Slate recording like any other: transcript, captions, and a title and
              summary written from what was said.
            </p>
            <p className="mt-2 text-caption text-chalk-faint">
              Coming from Loom?{' '}
              <AppLink to="/import/loom" onClick={onClose} className="text-chalk-dim underline-offset-2 hover:text-chalk hover:underline">
                Paste your Loom links
              </AppLink>{' '}
              and they come across as they are, transcripts included.
            </p>
          </>
        ) : (
          <div className="rounded-md border border-hairline bg-slate-900 px-5 py-8 text-center">
            <UploadIcon size={22} className="mx-auto mb-3 text-chalk-faint" />
            <p className="text-body text-chalk">Bring in any video</p>
            <p className="mx-auto mt-1 max-w-sm text-caption text-chalk-dim">
              A paid seat turns a video you already have into a Slate recording: transcript,
              captions, and a title and summary written from what was said.
            </p>
            <AppLink
              to={paywallPath('uploads')}
              onClick={() => {
                track('Paywall Opened', { feature: 'uploads' });
                onClose();
              }}
              className="mt-4 inline-block"
            >
              <Button variant="primary">Upgrade</Button>
            </AppLink>
            <p className="mt-4 text-caption text-chalk-faint">
              Coming from Loom?{' '}
              <AppLink to="/import/loom" onClick={onClose} className="text-chalk-dim underline-offset-2 hover:text-chalk hover:underline">
                See how your Loom library comes across
              </AppLink>
              .
            </p>
          </div>
        )}
      </Card>
    </Backdrop>
  );
}

// ---------------------------------------------------------------------------

function RecorderPanel({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { org } = useOrg();
  const go = useGo();
  const transport = useMemo(() => createUploadTransport(), []);
  const makeTitle = useCallback(() => defaultRecordingTitle(displayNameOf(user)), [user]);
  // Where the take will land: a live team plan takes it, otherwise it is the
  // person's own library.
  const workspace = org && orgAcceptsRecordings(org.subscriptionStatus) ? 'team' : 'personal';
  const session = useRecorderSession({ transport, makeTitle, workspace });
  const { recorder, mode, setMode, surface, setSurface } = session;
  const { phase } = recorder;
  const [uploading, setUploading] = useState(false);
  const wantStart = useRef(false);

  // The profile picture, for the bubble's other face.
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000 });
  useEffect(() => {
    const initials = displayNameOf(user)
      .split(/\s+/)
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
    recorder.setBubble({ avatarUrl: me.data?.avatarUrl ?? user?.photoURL ?? null, initials });
    // Only when the picture or the name changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.avatarUrl, user?.photoURL, user?.displayName]);

  // Names for the devices, and the permissions that come with them, asked
  // while the person is looking at the panel rather than mid countdown.
  useEffect(() => {
    void recorder.requestInputs();
    // Once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leave = useCallback(() => {
    recorder.releaseSources();
    onClose();
  }, [onClose, recorder]);

  /** Pick the screen if none is picked, then count down. One button. */
  const begin = useCallback(async () => {
    if (phase === 'ready') {
      recorder.start();
      return;
    }
    if (phase !== 'idle') return;
    wantStart.current = true;
    await recorder.arm();
  }, [phase, recorder]);

  useEffect(() => {
    if (phase === 'ready' && wantStart.current) {
      wantStart.current = false;
      recorder.start();
    }
    if (phase === 'idle') wantStart.current = false;
  }, [phase, recorder]);

  // Done and uploaded: the share page, as the Mac app opens it.
  useEffect(() => {
    if (phase !== 'preview') return;
    const shareId = recorder.shareId;
    if (!shareId || recorder.upload.error) return;
    recorder.releaseSources();
    onClose();
    go('/v/$shareId', { params: { shareId } });
  }, [go, onClose, phase, recorder]);

  const active = phase === 'recording' || phase === 'paused';
  const settingUp = phase === 'idle' || phase === 'arming' || phase === 'ready';

  // Full cover or a strip in the corner while the take runs. Camera only
  // has no strip to fold to: the camera is the whole picture.
  const [compact, setCompact] = useState(() => takeViewPreference.get() === 'compact');
  const canFold = surface !== 'camera';
  const setTakeView = (next: boolean) => {
    setCompact(next);
    takeViewPreference.set(next ? 'compact' : 'full');
  };

  useHotkeys(
    {
      space: () => {
        if (settingUp) void begin();
        else if (phase === 'countdown') recorder.skipCountdown();
        else if (active) recorder.togglePause();
      },
      enter: () => {
        if (active) void recorder.stop('hotkey');
      },
      'shift+r': () => {
        if (active) recorder.restart();
      },
      m: () => {
        if (active && canFold) setTakeView(!compact);
      },
      escape: () => {
        if (settingUp) leave();
        else if (phase === 'countdown') recorder.skipCountdown();
      },
    },
    !uploading,
  );

  if (uploading) {
    return (
      <Backdrop onDismiss={() => setUploading(false)}>
        <Card className="w-full max-w-md">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-body font-medium text-chalk">Upload a video</p>
            <CloseButton onClick={() => setUploading(false)} />
          </div>
          <FileUpload
            transport={transport}
            makeTitle={(file) => file.name.replace(/\.[^.]+$/, '') || makeTitle()}
            onUploaded={(result) => {
              leave();
              go('/v/$shareId', { params: { shareId: result.shareId } });
            }}
          />
        </Card>
      </Backdrop>
    );
  }

  if (phase === 'unsupported') {
    return (
      <Backdrop onDismiss={leave}>
        <Card className="w-full max-w-md">
          <p className="text-body font-medium text-chalk">This browser cannot record the screen</p>
          <p className="mt-1 text-caption text-chalk-dim">
            Chrome, Edge or Firefox on a desktop can. Or upload a video you already have.
          </p>
          <div className="mt-4 flex gap-2">
            <Button variant="primary" onClick={() => setUploading(true)}>
              Upload a file
            </Button>
            <Button onClick={leave}>Close</Button>
          </div>
        </Card>
      </Backdrop>
    );
  }

  if (settingUp) {
    return (
      <Backdrop
        onDismiss={phase === 'idle' ? leave : undefined}
        stage={surface === 'camera' ? <CameraStage recorder={recorder} /> : null}
      >
        {recorder.cameraWanted && recorder.cameraMonitor && surface !== 'camera' ? <Bubble recorder={recorder} /> : null}
        <SetupPanel
          recorder={recorder}
          mode={mode}
          setMode={setMode}
          surface={surface}
          setSurface={setSurface}
          planPaused={org ? !orgAcceptsRecordings(org.subscriptionStatus) : false}
          onStart={() => void begin()}
          onUpload={() => setUploading(true)}
          onClose={leave}
        />
      </Backdrop>
    );
  }

  if (phase === 'countdown') {
    // The curtain is down, edge to edge, and the number is up; the face
    // stays on top of both, right where it will be for the take.
    return (
      <div className="fixed inset-0 z-50" data-theme="dark">
        {surface === 'camera' ? <CameraStage recorder={recorder} /> : null}
        <Countdown value={recorder.countdown} onSkip={recorder.skipCountdown} />
        {recorder.cameraWanted && recorder.cameraMonitor && surface !== 'camera' ? <Bubble recorder={recorder} /> : null}
      </div>
    );
  }

  if (phase === 'preview') {
    // Only when the upload did not make it: the successful case has already
    // gone to the share page. The preview keeps the file and offers a retry.
    return (
      <Backdrop>
        <Card className="w-full max-w-2xl">
          <RecorderPreview
            recorder={recorder}
            defaultTitle={makeTitle()}
            onDone={leave}
            onRecordAnother={() => recorder.reset()}
          />
        </Card>
      </Backdrop>
    );
  }

  // Recording, paused, finishing, minimized: nothing dimmed, the page is
  // usable and showable. A strip in the corner, a face in the other.
  if (compact && canFold) {
    return (
      <div className="pointer-events-none fixed inset-0 z-50" data-theme="dark">
        <TakeStrip recorder={recorder} onExpand={() => setTakeView(false)} />
        {recorder.cameraWanted && recorder.cameraMonitor ? <Bubble recorder={recorder} /> : null}
        {recorder.upload.error && active ? (
          <div className="pointer-events-auto absolute right-4 top-20 w-[320px]">
            <Banner tone="danger" title="Upload is behind">
              {recorder.upload.error} The take keeps recording; it can be retried when you stop.
            </Banner>
          </div>
        ) : null}
      </div>
    );
  }

  // Recording, paused, finishing: the page is covered and this tab is in
  // recording mode until the take is over. What is being captured plays in
  // the middle, the controls sit under it, and there is nothing else to
  // wander off into. Camera only fills the page with the camera instead.
  return (
    <div className="fixed inset-0 z-50 bg-slate-950" data-theme="dark">
      {surface === 'camera' ? <CameraStage recorder={recorder} /> : <LivePreview recorder={recorder} />}
      <TakeControls
        recorder={recorder}
        showsScreen={surface !== 'camera'}
        onMinimize={canFold ? () => setTakeView(true) : undefined}
      />
      {recorder.upload.error && active ? (
        <div className="absolute right-4 top-4 w-[320px]">
          <Banner tone="danger" title="Upload is behind">
            {recorder.upload.error} The take keeps recording; it can be retried when you stop.
          </Banner>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Backdrop({
  children,
  onDismiss,
  stage,
}: {
  children: ReactNode;
  onDismiss?: () => void;
  /** Something to show under the dimming, in place of the page. */
  stage?: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50" data-theme="dark">
      {stage}
      <div
        className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={onDismiss}
        aria-hidden
      />
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4 [&>*]:pointer-events-auto">
        {children}
      </div>
    </div>
  );
}

function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('animate-fade-up rounded-xl border border-hairline bg-slate-900 p-5 shadow-raised', className)}>
      {children}
    </div>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="flex h-7 w-7 items-center justify-center rounded text-chalk-dim hover:bg-slate-800 hover:text-chalk"
    >
      <XIcon size={14} />
    </button>
  );
}

// ---------------------------------------------------------------------------

const SURFACES: { value: RecordSurface; icon: ReactNode; title: string; subtitle: string }[] = [
  { value: 'monitor', icon: <MonitorIcon size={16} />, title: 'Full screen', subtitle: 'Choose a screen' },
  { value: 'window', icon: <WindowGlyph />, title: 'Specific window', subtitle: 'Pick it when you start' },
  { value: 'camera', icon: <CameraIcon size={16} />, title: 'Camera only', subtitle: 'No screen, just you' },
];

function SetupPanel({
  recorder,
  mode,
  setMode,
  surface,
  setSurface,
  planPaused,
  onStart,
  onUpload,
  onClose,
}: {
  recorder: RecorderApi;
  mode: 'screen' | 'audio';
  setMode: (mode: 'screen' | 'audio') => void;
  surface: RecordSurface;
  setSurface: (surface: RecordSurface) => void;
  planPaused: boolean;
  onStart: () => void;
  onUpload: () => void;
  onClose: () => void;
}) {
  const [page, setPage] = useState<'main' | 'source'>('main');
  const arming = recorder.phase === 'arming';
  const audio = mode === 'audio';
  const chosen = SURFACES.find((s) => s.value === surface) ?? SURFACES[0];

  // The source page replaces the panel, as it does on the Mac: a header
  // with the way back, then the choices, the current one ticked.
  if (page === 'source') {
    return (
      <div className="absolute right-4 top-4 w-[288px] animate-fade-up rounded-xl border border-hairline bg-slate-900 p-3.5 shadow-raised">
        <div className="mb-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage('main')}
            aria-label="Back"
            className="flex h-7 w-7 items-center justify-center rounded text-chalk hover:bg-slate-800"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <span className="text-body font-semibold text-chalk">Record</span>
        </div>
        <div className="space-y-2">
          {SURFACES.map((option) => {
            const picked = !audio && option.value === surface;
            return (
              <Row
                key={option.value}
                icon={option.icon}
                title={option.title}
                subtitle={option.subtitle}
                emphasised={picked}
                right={
                  picked ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="text-chalk" aria-hidden>
                      <path d="M5 12l5 5 9-10" />
                    </svg>
                  ) : (
                    <ChevronRight />
                  )
                }
                onClick={() => {
                  track('Recording Source Selected', {
                    source: option.value === 'monitor' ? 'screen' : option.value,
                  });
                  setMode('screen');
                  setSurface(option.value);
                  setPage('main');
                }}
              />
            );
          })}
        </div>
      </div>
    );
  }
  const camera = recorder.cameraDevices.find((d) => d.deviceId === recorder.cameraDeviceId) ?? recorder.cameraDevices[0];
  const mic = recorder.micDevices.find((d) => d.deviceId === recorder.micDeviceId) ?? recorder.micDevices[0];

  return (
    <div className="absolute right-4 top-4 w-[288px] animate-fade-up rounded-xl border border-hairline bg-slate-900 p-3.5 shadow-raised">
      <div className="mb-3 flex items-center justify-between">
        <Wordmark />
        <CloseButton onClick={onClose} />
      </div>

      <div className="space-y-2">
        <Row
          icon={audio ? <WaveformIcon size={16} /> : chosen.icon}
          title={audio ? 'Audio note' : chosen.title}
          subtitle={audio ? 'Microphone only, no picture' : recorder.hasDisplay ? 'Ready to start' : chosen.subtitle}
          right={<ChevronRight />}
          onClick={() => setPage('source')}
        />

        {!audio && surface !== 'camera' ? (
          <Row
            icon={<CameraIcon size={16} />}
            title={camera?.label || 'Camera'}
            subtitle={
              !recorder.cameraWanted
                ? 'Off'
                : recorder.bubble.showAvatar
                  ? 'Your profile picture, where the bubble is'
                  : 'Drag the bubble anywhere'
            }
            right={<Pill on={recorder.cameraWanted} onClick={() => void recorder.toggleCamera()} />}
            picker={
              recorder.cameraDevices.length > 1 ? (
                <DevicePicker
                  devices={recorder.cameraDevices}
                  value={recorder.cameraDeviceId}
                  onChange={(id) => void recorder.selectCameraDevice(id)}
                  label="Camera"
                />
              ) : null
            }
          />
        ) : null}

        <Row
          icon={<MicIcon size={16} />}
          title={mic?.label || 'Microphone'}
          subtitle={recorder.micWanted ? <Meter stream={recorder.micMonitor} /> : 'Off'}
          right={audio ? null : <Pill on={recorder.micWanted} onClick={() => void recorder.toggleMic()} />}
          picker={
            recorder.micDevices.length > 1 ? (
              <DevicePicker
                devices={recorder.micDevices}
                value={recorder.micDeviceId}
                onChange={(id) => void recorder.selectMicDevice(id)}
                label="Microphone"
              />
            ) : null
          }
        />
      </div>

      {recorder.error ? <p className="mt-3 text-caption text-danger">{recorder.error}</p> : null}
      {planPaused ? (
        <p className="mt-3 text-caption text-chalk-faint">
          Your team's plan is paused, so this recording will be yours alone.
        </p>
      ) : null}

      <button
        type="button"
        onClick={onStart}
        disabled={arming}
        className={cx(
          'mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-record text-body font-semibold text-white transition-colors duration-120 hover:bg-record/90 disabled:opacity-60',
        )}
      >
        {arming ? <Spinner size={14} /> : <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white"><span className="h-1.5 w-1.5 rounded-full bg-record" /></span>}
        {arming ? 'Choosing what to record' : 'Start recording'}
      </button>

      <div className="mt-3 flex items-center justify-between text-caption text-chalk-faint">
        <span>Space to start</span>
        <span className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              track('Recording Source Selected', { source: audio ? 'screen' : 'audio' });
              setMode(audio ? 'screen' : 'audio');
            }}
            className="hover:text-chalk"
          >
            {audio ? 'Record the screen' : 'Audio note'}
          </button>
          <button type="button" onClick={onUpload} className="hover:text-chalk">
            Upload a file
          </button>
        </span>
      </div>
    </div>
  );
}

function ChevronRight() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className="text-chalk-faint" aria-hidden>
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}

function WindowGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 9h18" />
    </svg>
  );
}

function Row({
  icon,
  title,
  subtitle,
  right,
  onClick,
  picker,
  emphasised = false,
}: {
  icon: ReactNode;
  title: string;
  subtitle: ReactNode;
  right?: ReactNode;
  onClick?: () => void;
  picker?: ReactNode;
  emphasised?: boolean;
}) {
  const body = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center text-chalk">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="relative block truncate text-body text-chalk">
          {title}
          {picker}
        </span>
        <span className="mt-0.5 block text-caption text-chalk-faint">{subtitle}</span>
      </span>
      {right ? <span className="shrink-0">{right}</span> : null}
    </>
  );
  const className = cx('flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left', emphasised ? 'bg-slate-700/80' : 'bg-slate-800/70');
  return onClick ? (
    <button type="button" onClick={onClick} className={cx(className, 'hover:bg-slate-800')}>
      {body}
    </button>
  ) : (
    <div className={className}>{body}</div>
  );
}

/** An invisible select over the device name: the name is the control. */
function DevicePicker({
  devices,
  value,
  onChange,
  label,
}: {
  devices: { deviceId: string; label: string }[];
  value: string | null;
  onChange: (deviceId: string) => void;
  label: string;
}) {
  return (
    <select
      aria-label={label}
      value={value ?? devices[0]?.deviceId ?? ''}
      onChange={(event) => onChange(event.target.value)}
      className="absolute inset-0 cursor-pointer opacity-0"
    >
      {devices.map((device) => (
        <option key={device.deviceId} value={device.deviceId}>
          {device.label || label}
        </option>
      ))}
    </select>
  );
}

function Pill({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cx(
        'rounded-full px-2.5 py-1 text-caption font-semibold transition-colors duration-120',
        on ? 'bg-chalk text-slate-950' : 'bg-slate-700 text-chalk-dim hover:text-chalk',
      )}
    >
      {on ? 'On' : 'Off'}
    </button>
  );
}

/** The microphone, as a bar that moves. Silence is a flat bar, which says as much. */
function Meter({ stream }: { stream: MediaStream | null }) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) {
      setLevel(0);
      return;
    }
    const context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    let frame = 0;
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (const sample of data) {
        const d = (sample - 128) / 128;
        sum += d * d;
      }
      setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4));
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      void context.close();
    };
  }, [stream]);
  return (
    <span className="mt-1 block h-1 w-24 overflow-hidden rounded-full bg-slate-700" aria-hidden>
      <span className="block h-full rounded-full bg-signal transition-[width] duration-75" style={{ width: `${Math.round(level * 100)}%` }} />
    </span>
  );
}

// ---------------------------------------------------------------------------

/**
 * What the recording sees, letterboxed in the middle of the cover: the
 * screen on its own, with the live bubble laid over it at the size and
 * place the compositor gives it. Dragging it here moves it in the take.
 */
function LivePreview({ recorder }: { recorder: RecorderApi }) {
  const ref = useRef<HTMLVideoElement>(null);
  const stream = recorder.displayMonitor;
  const [frame, setFrame] = useState<Frame | null>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    void video.play().catch(() => {});

    // The picture's rectangle inside the element, which object-contain
    // letterboxes; measured again whenever either side changes.
    const measure = () => {
      const rect = video.getBoundingClientRect();
      const { videoWidth, videoHeight } = video;
      if (!videoWidth || !videoHeight || !rect.width || !rect.height) return;
      const scale = Math.min(rect.width / videoWidth, rect.height / videoHeight);
      const width = videoWidth * scale;
      const height = videoHeight * scale;
      setFrame({
        left: rect.left + (rect.width - width) / 2,
        top: rect.top + (rect.height - height) / 2,
        width,
        height,
      });
    };
    video.addEventListener('loadedmetadata', measure);
    video.addEventListener('resize', measure);
    window.addEventListener('resize', measure);
    const observer = new ResizeObserver(measure);
    observer.observe(video);
    measure();
    return () => {
      video.removeEventListener('loadedmetadata', measure);
      video.removeEventListener('resize', measure);
      window.removeEventListener('resize', measure);
      observer.disconnect();
      video.srcObject = null;
    };
  }, [stream]);

  return (
    <>
      <div className="absolute inset-x-0 top-0 bottom-36 flex items-center justify-center p-8">
        {stream ? (
          <video
            ref={ref}
            muted
            playsInline
            className="max-h-full max-w-full rounded-xl object-contain shadow-[0_0_0_1px_rgba(255,255,255,0.14),0_0_48px_rgba(255,255,255,0.16),0_0_140px_rgba(120,160,255,0.28)]"
          />
        ) : (
          <Spinner size={20} />
        )}
      </div>
      {/* A dim over the picture. Recording the screen this tab is on
          shows the tab inside itself inside itself; dimmed, the picture
          reads as a monitor rather than a hall of mirrors, and the glow
          says where the recording's edges are. The bubble stays bright. */}
      {frame ? (
        <div
          className="pointer-events-none absolute rounded-xl bg-slate-950/50"
          style={{ left: frame.left, top: frame.top, width: frame.width, height: frame.height }}
          aria-hidden
        />
      ) : null}
      {frame && recorder.cameraWanted && recorder.cameraMonitor ? <Bubble recorder={recorder} frame={frame} /> : null}
    </>
  );
}

/**
 * Pause, restart, stop, and the clock, centred under the preview so there
 * is no mistaking what this tab is for right now.
 */
function TakeControls({
  recorder,
  showsScreen,
  onMinimize,
}: {
  recorder: RecorderApi;
  showsScreen: boolean;
  /** Fold the cover to a strip. Absent for camera only, which has nothing to fold to. */
  onMinimize?: () => void;
}) {
  const recording = recorder.phase === 'recording';
  const paused = recorder.phase === 'paused';
  const finishing = recorder.phase === 'finishing';
  return (
    <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 pb-8">
      {onMinimize && !finishing ? (
        <button
          type="button"
          onClick={onMinimize}
          title="Minimize to a strip and use the page underneath (M)"
          className="absolute right-4 top-0 flex h-9 items-center gap-2 rounded-lg border border-hairline bg-slate-900/95 px-3 text-caption text-chalk-dim shadow-raised backdrop-blur hover:text-chalk"
        >
          <MinimizeGlyph />
          Minimize
        </button>
      ) : null}
      <div className="flex items-center gap-2 rounded-2xl border border-hairline bg-slate-900/95 p-2 shadow-raised backdrop-blur">
        <span className="flex items-center gap-2.5 px-3">
          <span
            className={cx('h-2.5 w-2.5 rounded-full', recording ? 'animate-record-pulse bg-record' : 'bg-chalk-faint')}
            aria-hidden
          />
          <span className="font-mono text-body text-chalk tnum" data-timecode>
            {finishing ? 'Finishing' : paused ? 'Paused' : clock(recorder.elapsedMs / 1000)}
          </span>
        </span>
        {finishing ? (
          <span className="px-3">
            <Spinner size={16} />
          </span>
        ) : (
          <>
            <StageButton label={paused ? 'Resume' : 'Pause'} hint="Space" onClick={() => recorder.togglePause()}>
              {paused ? <PlayIcon size={16} /> : <PauseIcon size={16} />}
            </StageButton>
            <StageButton label="Restart" hint="Shift R" onClick={() => recorder.restart()}>
              <RestartIcon size={16} />
            </StageButton>
            <button
              type="button"
              onClick={() => void recorder.stop('button')}
              className="flex h-11 items-center gap-2 rounded-xl bg-record px-5 text-body font-semibold text-white hover:bg-record/90"
            >
              <StopIcon size={14} />
              Stop
              <kbd className="ml-1 rounded bg-white/15 px-1.5 text-[11px] font-medium">Enter</kbd>
            </button>
          </>
        )}
      </div>
      {!finishing ? (
        <p className="rounded-full bg-slate-950/70 px-3 py-1 text-caption text-chalk-dim backdrop-blur">
          {showsScreen
            ? onMinimize
              ? 'Switch to what you are showing, or minimize this and show the page underneath.'
              : 'Switch to what you are showing. Come back here to pause or stop.'
            : 'Talk to the camera. Come back here to pause or stop.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The take, folded: clock and controls in the top corner, the page under it
 * live. Expand brings the cover back.
 */
function TakeStrip({ recorder, onExpand }: { recorder: RecorderApi; onExpand: () => void }) {
  const recording = recorder.phase === 'recording';
  const paused = recorder.phase === 'paused';
  const finishing = recorder.phase === 'finishing';
  return (
    <div className="pointer-events-auto absolute right-4 top-4 flex items-center gap-1 rounded-xl border border-hairline bg-slate-900/95 p-1.5 shadow-raised backdrop-blur">
      <span className="flex items-center gap-2 px-2">
        <span
          className={cx('h-2 w-2 rounded-full', recording ? 'animate-record-pulse bg-record' : 'bg-chalk-faint')}
          aria-hidden
        />
        <span className="font-mono text-caption text-chalk tnum" data-timecode>
          {finishing ? 'Finishing' : paused ? 'Paused' : clock(recorder.elapsedMs / 1000)}
        </span>
      </span>
      {finishing ? (
        <span className="px-2">
          <Spinner size={14} />
        </span>
      ) : (
        <>
          <StripButton label={paused ? 'Resume' : 'Pause'} hint="Space" onClick={() => recorder.togglePause()}>
            {paused ? <PlayIcon size={14} /> : <PauseIcon size={14} />}
          </StripButton>
          <StripButton label="Restart" hint="Shift R" onClick={() => recorder.restart()}>
            <RestartIcon size={14} />
          </StripButton>
          <button
            type="button"
            onClick={() => void recorder.stop('button')}
            title="Stop (Enter)"
            className="flex h-8 items-center gap-1.5 rounded-lg bg-record px-3 text-caption font-semibold text-white hover:bg-record/90"
          >
            <StopIcon size={12} />
            Stop
          </button>
          <StripButton label="Expand" hint="M" onClick={onExpand}>
            <ExpandGlyph />
          </StripButton>
        </>
      )}
    </div>
  );
}

function StripButton({
  label,
  hint,
  onClick,
  children,
}: {
  label: string;
  hint: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={`${label} (${hint})`}
      className="flex h-8 w-8 items-center justify-center rounded-lg text-chalk-dim hover:bg-slate-800 hover:text-chalk"
    >
      {children}
    </button>
  );
}

function MinimizeGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 14h6v6M20 10h-6V4M14 10l6-6M4 20l6-6" />
    </svg>
  );
}

function ExpandGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );
}

function StageButton({
  label,
  hint,
  onClick,
  children,
}: {
  label: string;
  hint: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={`${label} (${hint})`}
      className="flex h-11 items-center gap-2 rounded-xl px-3.5 text-body text-chalk-dim hover:bg-slate-800 hover:text-chalk"
    >
      {children}
      {label}
    </button>
  );
}


/**
 * The camera, as the Mac app shows it: a circle you can drag anywhere and
 * that stays where you leave it, held inside the window by its edges, the
 * same place the recording draws it. Controls appear beside it on hover:
 * hide, three sizes, mirror, and your profile picture in place of the
 * camera. The same bubble stands over the dimmed page while you set up and
 * over the live page while you record.
 */
/**
 * Camera only: the whole camera picture, as large as the page allows and
 * letterboxed rather than cropped, exactly as it goes into the recording.
 * Not mirrored, so what you see is what viewers get.
 */
function CameraStage({ recorder }: { recorder: RecorderApi }) {
  const stream = recorder.monitor ?? recorder.cameraMonitor;
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    void video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [stream]);

  return (
    <div className="absolute inset-0 bg-slate-950">
      {stream ? (
        <video
          ref={ref}
          muted
          playsInline
          className="h-full w-full object-contain"
        />
      ) : (
        <p className="absolute inset-0 flex items-center justify-center text-caption text-chalk-faint">
          Waiting for the camera
        </p>
      )}
    </div>
  );
}

const SIZES = [
  { name: 'Small', scale: 0.16, px: 112 },
  { name: 'Medium', scale: 0.24, px: 160 },
  { name: 'Large', scale: 0.36, px: 224 },
] as const;
const EDGE = 20;

/** A rectangle on the page, in viewport pixels. */
interface Frame {
  left: number;
  top: number;
  width: number;
  height: number;
}

function Bubble({ recorder, frame }: { recorder: RecorderApi; frame?: Frame }) {
  const { bubble, cameraMonitor } = recorder;
  const ref = useRef<HTMLVideoElement>(null);
  const [hover, setHover] = useState(false);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const size = SIZES.reduce((best, s) => (Math.abs(s.scale - bubble.scale) < Math.abs(best.scale - bubble.scale) ? s : best), SIZES[1]);

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const video = ref.current;
    if (!video || !cameraMonitor) return;
    video.srcObject = cameraMonitor;
    void video.play().catch(() => {});
    return () => {
      video.srcObject = null;
    };
  }, [cameraMonitor, bubble.showAvatar]);

  if (!bubble.visible) {
    return (
      <button
        type="button"
        onClick={() => recorder.setBubble({ visible: true })}
        className="pointer-events-auto absolute bottom-5 left-5 z-30 rounded-full border border-hairline bg-slate-900/95 px-3 py-1.5 text-caption text-chalk-dim hover:text-chalk"
      >
        Show camera
      </button>
    );
  }

  // Over the page the bubble is a fixed on-screen size; over a frame (the
  // live picture on the recording cover) it is the size and margin the
  // compositor uses, so it sits exactly where it will be in the recording.
  const area: Frame = frame ?? { left: 0, top: 0, width: viewport.w, height: viewport.h };
  const px = frame ? Math.round(frame.height * bubble.scale) : size.px;
  const margin = frame ? Math.round(frame.height * 0.035) : EDGE;
  const r = px / 2;
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
  // Where the centre is: under the pointer while dragging, else where it
  // was left, and never past an edge either way.
  const wanted = drag ?? { x: area.left + bubble.anchor.x * area.width, y: area.top + bubble.anchor.y * area.height };
  const centerX = clamp(wanted.x, area.left + margin + r, area.left + area.width - margin - r);
  const centerY = clamp(wanted.y, area.top + margin + r, area.top + area.height - margin - r);
  const controlsAbove = centerY > area.top + area.height / 2;

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('[data-controls]')) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ x: event.clientX, y: event.clientY });
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (drag) setDrag({ x: event.clientX, y: event.clientY });
  };
  const onPointerUp = () => {
    if (!drag) return;
    const anchor = { x: (centerX - area.left) / area.width, y: (centerY - area.top) / area.height };
    setDrag(null);
    recorder.setBubble({ anchor, corner: cornerFor(anchor) });
  };

  const initials = bubble.initials || '?';

  return (
    <div
      className={cx('pointer-events-auto absolute z-30 flex select-none items-center', controlsAbove ? 'flex-col-reverse' : 'flex-col', drag ? 'cursor-grabbing' : 'cursor-grab')}
      style={{ left: centerX - r, top: controlsAbove ? undefined : centerY - r, bottom: controlsAbove ? viewport.h - (centerY + r) : undefined, width: px }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => setDrag(null)}
    >
      {bubble.showAvatar ? (
        bubble.avatarUrl ? (
          <img
            src={bubble.avatarUrl}
            alt=""
            referrerPolicy="no-referrer"
            draggable={false}
            style={{ width: px, height: px }}
            className="rounded-full object-cover shadow-[0_18px_40px_rgba(0,0,0,0.55)] ring-1 ring-white/15"
          />
        ) : (
          <span
            style={{ width: px, height: px, fontSize: px * 0.4 }}
            className="flex items-center justify-center rounded-full bg-slate-800 font-semibold text-chalk shadow-[0_18px_40px_rgba(0,0,0,0.55)] ring-1 ring-white/15"
          >
            {initials}
          </span>
        )
      ) : (
        <video
          ref={ref}
          muted
          playsInline
          style={{ width: px, height: px }}
          className={cx('rounded-full object-cover shadow-[0_18px_40px_rgba(0,0,0,0.55)] ring-1 ring-white/15', bubble.mirror && '-scale-x-100')}
        />
      )}
      <div
        data-controls
        className={cx(
          'flex w-max items-center gap-0.5 rounded-lg border border-hairline bg-slate-900/95 px-1 py-1 transition-opacity duration-120',
          controlsAbove ? 'mb-2' : 'mt-2',
          hover && !drag ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <BubbleControl label="Hide camera" onClick={() => void recorder.toggleCamera()}>
          <XIcon size={12} />
        </BubbleControl>
        {SIZES.map((option) => (
          <BubbleControl
            key={option.name}
            label={option.name}
            active={option.name === size.name}
            onClick={() => {
              track('Setting Changed', { setting: 'bubble_size', value: option.name.toLowerCase() });
              recorder.setBubble({ scale: option.scale });
            }}
          >
            <span className="block rounded-full bg-current" style={{ width: 4 + SIZES.indexOf(option) * 3, height: 4 + SIZES.indexOf(option) * 3 }} />
          </BubbleControl>
        ))}
        <BubbleControl
          label={bubble.mirror ? 'Stop mirroring' : 'Mirror'}
          active={bubble.mirror}
          onClick={() => {
            track('Setting Changed', { setting: 'bubble_mirror', value: !bubble.mirror });
            recorder.setBubble({ mirror: !bubble.mirror });
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 3v18" />
            <path d="M8 8l-4 4 4 4" />
            <path d="M16 8l4 4-4 4" />
          </svg>
        </BubbleControl>
        <BubbleControl
          label={bubble.showAvatar ? 'Show camera' : 'Show profile picture'}
          active={bubble.showAvatar}
          onClick={() => {
            track('Setting Changed', { setting: 'bubble_avatar', value: !bubble.showAvatar });
            recorder.setBubble({ showAvatar: !bubble.showAvatar });
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" />
          </svg>
        </BubbleControl>
      </div>
    </div>
  );
}

function BubbleControl({
  label,
  active = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-120',
        active ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:bg-slate-800 hover:text-chalk',
      )}
    >
      {children}
    </button>
  );
}
